import * as z from 'zod'
import { TRANSFORMATIONS } from '~/common/photos/transformations'
import { getTransformObject } from '~/common/photos/transforms'
import { getURLSearchParams } from '~/utility/url-params'
import { langFallback$Fetch } from '../../../lang/utils/langfetch'
import { moneyZ } from '../schema'
import {
  BookingData,
  DataRequestParams,
  bookingInfoZ,
  bookingQuoteZ,
  dataRequestParamsZ,
  timeZ,
} from './schema'

export class BookingDataServiceFetch {
  async get(
    request: DataRequestParams,
    langCode: string,
  ): Promise<BookingData | null> {
    const params = getURLSearchParams(
      paramsTransformer.parse(request),
    ).toString()
    const result: any = await langFallback$Fetch(
      langCode,
      `/_/endpoints/booking/data/?${params}`,
    )
    // when no price/quote set the root to null so zod works
    if (
      result &&
      result.quote &&
      result.quote.result &&
      !result.quote.result.price
    ) {
      result.quote = null
    }
    const parsedResult = responseTransformer.parse(result)
    if (
      result?.quote?.result?.compulsoryExtras?.length &&
      parsedResult?.quote?.extras?.length
    ) {
      // fix compulsory extras incase backend does not have compulsory yet
      const compulsoryIds = result.quote.result.compulsoryExtras.map(
        (extra) => extra.extra.id,
      )
      parsedResult.quote.extras.forEach((extra) => {
        if (compulsoryIds.includes(extra.id)) {
          extra.isCompulsory = true
        }
      })
    }
    return parsedResult
  }
}

// transform from app params to ajax params
const paramsTransformer = dataRequestParamsZ.transform((params) => {
  return {
    arrive: params.stayRange.start,
    depart: params.stayRange.end,
    adults: params.party.adults,
    childAges: params.party.childAges.join(','),
    dogs: params.party.dogs,
    pitchtype: params.pitchtypeId.toString(),
    chargetype: params.chargetypeId
      ? params.chargetypeId.toString()
      : undefined,
    selectedExtras: JSON.stringify(params.selectedExtras),
  }
})

const categoryZ = z.object({
  name: z.string(),
  slug: z.string(),
})

const infoResponseZ = bookingInfoZ
  .pick({
    // avoid duplicate definition when they are the same
    dimensionsType: true,
    dimensionsWidth: true,
    dimensionsDepth: true,
    dimensionsDetailed: true,
    subcategoriesRequiringDimensions: true,
    enforceSpecialRequests: true,
    isByo: true,
    oneUnitPerPitch: true,
    requireCarRegistration: true,
    requireEstimatedTimeOfArrival: true,
    requirePartyNames: true,
    requestGroupName: true,
    arrivalFrom: true,
    arrivalTo: true,
    departFrom: true,
  })
  .extend({
    categories: categoryZ
      .array()
      .transform((categories) =>
        categories.map((category) => ({ ...category, isPositive: true })),
      ),
    negativeCategories: categoryZ
      .array()
      .transform((categories) =>
        categories.map((category) => ({ ...category, isPositive: false })),
      ),
    dimensions: z.string().nullable(),
    dimensionsExample: z.string().nullable(),
    name: z.string(),
    allowedSubcategories: z
      .object({
        name: z.string(),
        allowed: z.boolean(),
      })
      .array(),
    primaryPhoto: z
      .object({
        url: z.object({
          masterImage: z.string(),
        }),
      })
      .nullable(),
    arrivalFromObj: timeZ,
    arrivalToObj: timeZ,
    departFromObj: timeZ,
    campsite: z.object({
      ageWarning: z.string().nullable(),
      allowImmediatePayment: z.boolean(),
      arrivalTaxes: z.union([
        z
          .object({
            amount: moneyZ,
            rate: z.number().optional(),
            text: z.string(),
            applicable: z.string(),
            maxNights: z.number().nullable(),
            minAge: z.number().nullable(),
            type: z.string(),
          })
          .array()
          .transform((taxes) =>
            taxes.map((tax) => ({
              ...tax,
              applicable: tax.applicable.toLowerCase() === 'applicable',
            })),
          ),
        z.null().transform(() => []),
      ]),
      arrivalTime: z.string(),
      cancellationPolicyNeedsTranslation: z.boolean().optional(),
      cityTax: z.string(),
      covidMeasures: z.string(),
      covidWarnings: z.string().array().optional(),
      exclusiveTaxes: z
        .object({
          rate: z.number(),
          text: z.string(),
        })
        .array()
        .transform((taxes) => taxes.map((tax) => ({ ...tax, name: tax.text }))),
      fullAddress2: z.string(),
      lastBooked: z.string().nullable(),
      maxChildAge: z.number(),
      name: z.string(),
      notices: z.string(),
      noticesNeedsTranslation: z.boolean().optional(),
      campsiteNotices: z
        .object({
          isCovid: z.boolean(),
          text: z.string(),
        })
        .array(),
      path: z.string(),
      paymentTiming: z.string(),
      policies: z.string(),
      rateCount: z.number(),
      rating10: z.number(),
      slug: z.string(),
      usefulInfo: z.string(),
      termsUsefulInfo: z.string().array(),
      termsAndConditions: z.string(),
      termsAndConditionsNeedsTranslation: z.boolean().optional(),
      timezone: z.string(),
    }),
    isEstimatedTimeOfArrivalCompulsory: z
      .boolean()
      .nullable()
      .optional()
      .transform((v) => !!v),
  })

// extra in the ajax results
const extraZ = z.object({
  pricingDisplay: z.string(),
  price: moneyZ,
  pitchtypeExtra: z
    .object({
      included: z.number().nullable(),
      maximum: z.number().nullable(),
    })
    .optional()
    .nullable(),
  extra: z.object({
    description: z.string(),
    descriptionNeedsTranslation: z.boolean().optional(),
    id: z.string(),
    max: z
      .number()
      .nullable()
      .transform((m) => (m === null ? Number.POSITIVE_INFINITY : m)),
    name: z.string(),
    terms: z.string().array().nullable().optional(),
  }),
})

const quoteResponseZ = z.object({
  balanceDueDate: z.string().nullable(),
  refundUntilDate: z.string().nullable().optional(),
  commission: z.number(),
  paymentWhen: bookingQuoteZ.shape.paymentWhen,
  paypalEnabled: z.boolean(),
  result: z.object({
    arrivalTaxes: z
      .object({
        amount: moneyZ,
        hidden: z.boolean(),
        type: z.string(),
      })
      .array()
      .transform((arrivalTaxes) =>
        arrivalTaxes.map((arrivalTax) => {
          return {
            isHidden: arrivalTax.hidden,
            ...arrivalTax,
          }
        }),
      ),
    chargeTypeId: z.number().transform((val) => val.toString()),
    compulsoryExtras: extraZ.array().transform((extras) =>
      extras.map((extra) => {
        return {
          ...extra,
          ...extra.extra,
          isCompulsory: true,
          descriptionNeedsTranslation:
            !!extra.extra.descriptionNeedsTranslation,
        }
      }),
    ),
    deposit: moneyZ,
    extras: extraZ.array().transform((extras) =>
      extras.map((extra) => {
        return {
          ...extra,
          ...extra.extra,
          isCompulsory: extra.compulsory || false,
          descriptionNeedsTranslation:
            !!extra.extra.descriptionNeedsTranslation,
        }
      }),
    ),
    // key is the extra id, and value is the quantity
    extrasInitial: z.record(z.number()),
    price: moneyZ,
    priceAccommodation: moneyZ,
    remainder: moneyZ,
    party: z.object({
      adults: z.number(),
      childAges: z.number().array(),
      children: z.number(),
      convertedChildrenToAdult: z.number(),
      infants: z.number(),
      maxChildAge: z.number(),
      maxInfantAge: z.number(),
      originalAdults: z.number(),
      size: z.number(),
      converted: z.object({
        adults: z.number(),
        children: z.number(),
        childrenAges: z.number().array(),
        infants: z.number(),
        infantsAges: z.number().array(),
      }),
    }),
    quote: z.object({
      pitchesToSell: z.number(),
    }),
    taxes: z
      .object({
        amount: moneyZ,
        commissionable: z.boolean(),
        hidden: z.boolean(),
        inclusive: z.boolean(),
        name: z.string(),
        rate: z.number(),
        taxType: z.string(),
        type: z.string(),
      })
      .array()
      .transform((taxes) =>
        taxes.map((tax) => {
          return {
            ...tax,
            isCommissionable: tax.commissionable,
            isHidden: tax.hidden,
            isInclusive: tax.inclusive,
          }
        }),
      ),
  }),
})

const ajaxResponseZ = z.object({
  info: infoResponseZ,
  quote: quoteResponseZ.nullable(),
  stripe_token: z
    .object({
      customer_id: z.string().optional().nullable(),
    })
    .optional()
    .nullable(),
})

const responseTransformer = ajaxResponseZ.transform((response) => {
  const info = response.info
  const stripeToken = response.stripe_token || {}
  const parsedInfo = {
    ...info.campsite,
    ...info,
    address: info.campsite.fullAddress2,
    allowImmediatePayment: info.campsite.allowImmediatePayment,
    campsiteName: info.campsite.name,
    campsiteTimezone: info.campsite.timezone,
    campsiteTermsAndConditions: info.campsite.termsAndConditions,
    cancellationPolicy: info.campsite.policies,
    cancellationPolicyNeedsTranslation:
      !!info.campsite.cancellationPolicyNeedsTranslation,
    covidMeasures: info.campsite.covidMeasures,
    noticesNeedsTranslation: !!info.campsite.noticesNeedsTranslation,
    rating: {
      rate: info.campsite.rating10,
      votes: info.campsite.rateCount,
    },
    categories: info.categories.concat(info.negativeCategories),
    pitchTypeName: info.name,
    pitchTypeDimensions: info.dimensions,
    pitchTypeDimensionsExample: info.dimensionsExample,
    allowedSubcategories: info.allowedSubcategories
      .filter((subCat) => subCat.allowed)
      .map((subCat) => subCat.name),
    disallowedSubcategories: info.allowedSubcategories
      .filter((subCat) => !subCat.allowed)
      .map((subCat) => subCat.name),
    photo: {
      url: getTransformObject<(typeof TRANSFORMATIONS)['bookingCampsite']>(
        'bookingCampsite',
        info.primaryPhoto?.url.masterImage || '',
      ),
    },
    taxes: {
      arrival: info.campsite.arrivalTaxes,
      city: info.campsite.cityTax,
      exclusive: info.campsite.exclusiveTaxes,
    },
    termsAndConditionsNeedsTranslation:
      !!info.campsite.termsAndConditionsNeedsTranslation,
    warnings: info.campsite.covidWarnings ? info.campsite.covidWarnings : [],
  }
  const quote = response.quote
  const parsedQuote = quote
    ? {
        ...quote,
        ...quote.result,
        ...quote.result.quote,
        chargetypeId: quote.result.chargeTypeId,
        convertedChildren: quote.result.party.convertedChildrenToAdult,
        priceTotal: quote.result.price,
        pricedTax: quote.result.taxes,
        extras: quote.result.extras,
        extrasInitial: quote.result.extrasInitial,
        compulsoryExtras: quote.result.compulsoryExtras,
        convertedParty: quote.result.party.converted,
      }
    : null

  return {
    info: parsedInfo,
    quote: parsedQuote,
    stripeToken,
  }
})
